import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Accounts
export interface AccountFilters {
  search?: string;
  customer_id?: string;
  supplier_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export function useAccounts(filters?: AccountFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['accounts', currentWorkspace?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'list_accounts', filters },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useAccount(accountId: string) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['account', currentWorkspace?.id, accountId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'get_account', account_id: accountId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id && !!accountId,
  });
}

export function useCreateAccount() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (accountData: any) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'create_account', account: accountData },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', currentWorkspace?.id] });
      toast({ title: 'Account created successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create account',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateAccount() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ accountId, updates }: { accountId: string; updates: any }) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'update_account', account_id: accountId, updates },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['account'] });
      toast({ title: 'Account updated successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update account',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteAccount() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (accountId: string) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'delete_account', account_id: accountId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', currentWorkspace?.id] });
      toast({ title: 'Account deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete account',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Sites
export interface SiteFilters {
  search?: string;
  customer_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export function useSitesAPI(filters?: SiteFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['sites-api', currentWorkspace?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'list_sites', filters },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useSite(siteId: string) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['site', currentWorkspace?.id, siteId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'get_site', site_id: siteId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id && !!siteId,
  });
}

export function useCreateSite() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (siteData: any) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'create_site', site: siteData },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites-api', currentWorkspace?.id] });
      toast({ title: 'Site created successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create site',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateSite() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ siteId, updates }: { siteId: string; updates: any }) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'update_site', site_id: siteId, updates },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites-api', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['site'] });
      toast({ title: 'Site updated successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update site',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteSite() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (siteId: string) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'delete_site', site_id: siteId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites-api', currentWorkspace?.id] });
      toast({ title: 'Site deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete site',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Entities
export interface EntityFilters {
  search?: string;
  customer_id?: string;
  site_id?: string;
  account_id?: string;
  identifier_type?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export function useEntities(filters?: EntityFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['entities', currentWorkspace?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'list_entities', filters },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useEntity(entityId: string) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['entity', currentWorkspace?.id, entityId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'get_entity', entity_id: entityId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id && !!entityId,
  });
}

export function useCreateEntity() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (entityData: any) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'create_entity', entity: entityData },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities', currentWorkspace?.id] });
      toast({ title: 'Entity created successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create entity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateEntity() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ entityId, updates }: { entityId: string; updates: any }) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'update_entity', entity_id: entityId, updates },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['entity'] });
      toast({ title: 'Entity updated successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update entity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteEntity() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (entityId: string) => {
      const { data, error } = await supabase.functions.invoke('onboarding-manage', {
        body: { action: 'delete_entity', entity_id: entityId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities', currentWorkspace?.id] });
      toast({ title: 'Entity deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to delete entity',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
